<!--
  - Copyright (c) ProviderTrust LLC.
  - All Rights Reserved.
  -
  - This software is confidential and proprietary information of
  - ProviderTrust LLC ("Confidential Information"). You shall not disclose
  - such Confidential Information and shall use it only in
  - accordance with the terms of the license agreement you entered
  - into with ProviderTrust.
-->

<template>
  <div class="flex-search-details-ssdmf">
    <div class="operations-card">
      <div class="left-card">
        <div class="form-group">
          <div class="btn-group">
            <ec-dropdown>
              <v-btn color="white" slot="activator">
                <v-icon color="primary">fa-columns</v-icon>
              </v-btn>

              <div class="dropdown-wrapper app-select-options">
                <h3>Select Columns to Display</h3>

                <v-select-list
                  :items="headerOptions"
                  action
                  :selected-items="selectedHeaderValues"
                  color="primary"
                  @select="updateSelectedHeaderValues"
                />
              </div>
            </ec-dropdown>
          </div>
        </div>
        <div class="form-group">
          <label>Search criteria</label>
          <v-text-field
            class="filter search-filter"
            solo
            v-model="searchCriteriaFilter"
            @input="debouncedSearch(true)"
          />
        </div>
        <div class="form-group">
          <label>Record Status</label>
          <v-select
            class="filter"
            content-class="app-select-options"
            :items="recordFilterOptions"
            v-model="recordFilter"
            multiple
            solo
            @input="debouncedSearch(true)"
          >
            <template slot="selection" slot-scope="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.text }}</span>
              </v-chip>
              <span v-if="index === 1" class="grey--text caption"
                >(+{{ recordFilter.length - 1 }} others)</span
              >
            </template>
          </v-select>
        </div>
        <div class="form-group">
          <label>SSDMF status</label>
          <v-select
            class="filter"
            content-class="app-select-options"
            :items="ssdmfStatusOptions"
            v-model="ssdmfFilter"
            :multiple="false"
            clearable
            solo
            @input="debouncedSearch()"
          >
            <template slot="selection" slot-scope="{ item }">
              <v-chip>
                <span>{{ item.text }}</span>
              </v-chip>
            </template>
          </v-select>
        </div>
        <v-btn color="primary" class="reset-btn" @click="resetFilters">
          Reset
        </v-btn>
      </div>

      <status-dropdown-dialog
        class="bulk-status-dropdown"
        :disabled="!selectedItems.length"
        @status-change="
          saveBulkStatusChange($event, API.FLEXBATCH_SSDMF_DETAILS_BYIDS)
        "
        :status-infos="statusInfos"
        :status-change-loading="statusChangeLoading"
      >
        Bulk Status Change
      </status-dropdown-dialog>
    </div>

    <v-data-table
      ref="ssdmftable"
      class="ec-table ssdmf-table"
      v-model="selectedItems"
      :headers="selectedHeaders"
      :items="items"
      select-all
      sort-icon="arrow_drop_up"
      disable-initial-sort
      hide-actions
    >
      <template slot="items" slot-scope="props">
        <tr
          @click="
            updateSlideIn(props.item.id, props.item.status, props.item.searchId)
          "
          :key="props.item.id"
        >
          <td @click.stop>
            <v-checkbox
              color="primary"
              v-if="props.item.id"
              v-model="props.selected"
              hide-details
            />
          </td>
          <td v-show="isHeaderSelected('ssn')">
            <div class="table-cell-wrapper">{{ props.item.ssn }}</div>
          </td>
          <td v-show="isHeaderSelected('name')">
            <div class="table-cell-wrapper">{{ props.item.name }}</div>
          </td>
          <td
            :style="{ color: statusColor(props.item.ssdmfStatus) }"
            v-show="isHeaderSelected('ssdmfStatus')"
          >
            {{ props.item.ssdmfStatus }}
          </td>
          <td v-show="isHeaderSelected('status')" class="dropdown-column">
            <status-dropdown-dialog
              v-if="props.item.status"
              @click.capture.stop.prevent
              :status="props.item.status"
              :status-infos="statusInfos"
              @status-change="
                saveStatusChange(
                  $event,
                  [props.item.id],
                  API.FLEXBATCH_SSDMF_DETAILS_BYIDS
                )
              "
              :bottom-up-dropdown="bottomUpDropdowns[props.index]"
              :bottom-up-confirmdialog="bottomUpConfirmdialogs[props.index]"
              @show-dropdown="
                updateBottomUp(
                  $event,
                  'bottomUpDropdowns',
                  props.index,
                  tableQuery
                )
              "
              @show-confirmdialog="
                updateBottomUp(
                  $event,
                  'bottomUpConfirmdialogs',
                  props.index,
                  tableQuery
                )
                hasConfirmdialogOpened = $event
              "
              :force-dropdown-closed="hasConfirmdialogOpened"
            />
            <div v-else class="clear-status">Clear</div>
          </td>
          <td v-show="isHeaderSelected('latestNote')">
            {{ props.item.latestNote }}
          </td>
          <td v-show="isHeaderSelected('ssdmfName')">
            {{ props.item.ssdmfName }}
          </td>
          <td v-show="isHeaderSelected('dateOfBirth')">
            {{ props.item.dateOfBirth }}
          </td>
          <td v-show="isHeaderSelected('dateOfDeath')">
            {{ props.item.dateOfDeath }}
          </td>
        </tr>
      </template>
    </v-data-table>

    <div class="text-xs-center pt-2">
      <v-pagination v-model="page" :length="pages" :total-visible="7" />
    </div>
  </div>
</template>

<script>
import {
  API,
  createFilterList,
  RECORDS_PER_PAGE,
  SEARCH_DEBOUNCE_TIMEOUT,
  statusColor,
  statusInfos
} from "@/common/constants"
import { mapMutations } from "vuex"
import { debounce } from "lodash"
import EventBus from "@/common/event-bus"
import axios from "axios"
import EcDropdown from "@/components/Dropdown"
import { deleteElFromArry } from "@/service/util"
import StatusDropdownDialog from "@/components/StatusDropdownDialog"
import saveStatusChange from "@/mixins/statusChange"
import flipDialogUp from "@/mixins/flipDialogUp"

const thePromise = Promise.resolve()

const ssdmfFilters = {
  true: { text: "Found on SSDMF" },
  false: { text: "Not Found on SSDMF" }
}

const initShowingHeaders = [
  {
    value: "ssdmfStatus",
    width: "180px",
    text: "SSDMF Status"
  },
  {
    value: "status",
    width: "180px",
    text: "Status"
  },
  {
    value: "latestNote",
    width: "180px",
    text: "Latest Notes"
  }
]

const initNotShowingHeaders = [
  {
    value: "ssdmfName",
    width: "180px",
    text: "SSDMF Name"
  },
  {
    value: "dateOfBirth",
    width: "120px",
    text: "Date Of Birth"
  },
  {
    value: "dateOfDeath",
    width: "120px",
    text: "Date Of Death"
  }
]

const fixedHeaders = [
  {
    value: "ssn",
    width: "140px",
    text: "SSN"
  },
  {
    value: "name",
    width: "200px",
    text: "Name"
  }
]

const availableHeaders = initShowingHeaders.concat(initNotShowingHeaders)
const eventModifier = "SSDMF"

export default {
  name: "FlexSearchDetailsSSDMF",
  displayName: "SSDMF",

  props: {
    batchId: {
      type: String,
      default: "-1"
    }
  },
  data: () => ({
    page: 0,
    pages: 1,

    items: [],
    selectedItems: [],
    headerOptions: availableHeaders,
    selectedHeaderValues: fixedHeaders
      .concat(initShowingHeaders)
      .map(({ value }) => value),
    showNameSearch: false,
    hasConfirmdialogOpened: false,
    bottomUpDropdowns: [],
    bottomUpConfirmdialogs: [],
    tableQuery: ".ssdmf-table table.v-datatable",
    currentSlideInId: -1,

    statusColor,
    statusInfos,
    recordFilter: [],
    ssdmfFilters,
    ssdmfFilter: null,
    searchCriteriaFilter: "",
    API
  }),
  mixins: [saveStatusChange, flipDialogUp],

  methods: {
    ...mapMutations([
      "setSlideIn",
      "setStatusInfos",
      "hasClearNonBtn",
      "setEventModifier"
    ]),
    debouncedSearch: debounce(function(newSearch) {
      thePromise.then(() => this.search(newSearch))
    }, SEARCH_DEBOUNCE_TIMEOUT),
    initPage() {
      this.page = 1
      this.selectedItems = []
    },
    search(newSearch = false) {
      this.loading = true

      if (newSearch) {
        this.initPage()
      }

      const page = this.page
      console.log(
        `Fetching flexible batch search details page: ${page} for batchId: ${
          this.batchId
        }, newSearch: ${newSearch}`
      )

      axios
        .get(API.FLEXBATCH_SSDMF_DETAILS.replace(":batchId", this.batchId), {
          params: {
            limits: RECORDS_PER_PAGE,
            page,
            requireTotal: newSearch,
            searchCriteria: this.searchCriteriaFilter,
            ssdmfStatus: this.ssdmfFilter,
            recordStatus: this.recordFilter
          }
        })
        .then(response => {
          if (newSearch)
            this.pages = Math.ceil(response.data.total / RECORDS_PER_PAGE)

          this.items = response.data.ssdmfResults
          this.showNameSearch = response.data.nameSearch
        })
        .finally(() => (this.loading = false))
    },
    resetFilters() {
      this.recordFilter = []
      this.ssdmfFilter = null
      this.searchCriteriaFilter = ""
      this.debouncedSearch(true)
    },
    isHeaderSelected(value) {
      if (value === "name") {
        return this.showNameSearch
      }
      return this.selectedHeaderValues.includes(value)
    },
    updateSelectedHeaderValues({ value }) {
      const arry = this.selectedHeaderValues
      deleteElFromArry(arry, value) || arry.push(value)
    },
    updateSlideIn(id, matchstatus, searchId) {
      this.currentSlideInId = id
      if (id === null) {
        id = -1
      }
      axios
        .get(
          API.FLEXBATCH_SSDMF_DETAILS_BYID.replace(
            ":batchId",
            this.batchId
          ).replace(":id", id),
          {
            params: {
              searchId: searchId
            }
          }
        )
        .then(response => {
          this.setSlideIn(response.data)
          this.setStatusInfos(statusInfos)
          this.hasClearNonBtn(matchstatus === null)
          this.setEventModifier(eventModifier)
        })
    }
  },

  watch: {
    selectedItems: function(oldList, newList) {
      oldList.filter(item => {
        if (item.id === null) {
          oldList.pop(item)
        }
      })
    },
    page(oldVal, newVal) {
      if (!this.loading) this.search()
    }
  },

  computed: {
    selectedHeaders() {
      return fixedHeaders
        .concat(availableHeaders)
        .filter(({ value }) => this.isHeaderSelected(value))
    },
    recordFilterOptions() {
      return createFilterList(this.statusInfos)
    },
    ssdmfStatusOptions() {
      return createFilterList(this.ssdmfFilters)
    }
  },
  beforeDestroy() {
    EventBus.$off("global-status-change" + eventModifier)
  },
  created() {
    this.search(true)
    EventBus.$on("global-status-change" + eventModifier, evt => {
      const id = this.currentSlideInId
      if (id !== -1) {
        this.saveStatusChange(
          evt,
          [id],
          API.FLEXBATCH_SSDMF_DETAILS_BYIDS
        ).then(() => this.updateSlideIn(id))
      }
    })
  },
  components: { StatusDropdownDialog, EcDropdown }
}
</script>

<style lang="scss" scoped>
.flex-search-details-ssdmf {
  padding: 2.25rem;
  padding-bottom: 1rem;

  .ssdmf-table tr:not(.v-datatable__group-row) {
    &:nth-child(odd) {
      background-color: $color-off-white;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/FlexSearchDetails-SSDMF.vue